import React, {
  useRef,
  useState,
  RefObject,
  useEffect,
  useCallback,
} from 'react';
import styled, { css } from 'styled-components/macro';
import Tippy from '@tippyjs/react';
import { Instance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import colors from 'styles/themes/colors';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { ReactComponent as IconCopy } from 'icons/icon-copy.svg';
import { scrollBar } from 'styles/base-classes';

const StyledTippy = styled(Tippy)<any>`
  background: ${colors.NightBlue} !important;
  font-weight: 600;
  white-space: pre-line;

  .tippy-arrow {
    color: ${colors.NightBlue};
  }
  /* Styling the arrow for different placements */
  &[data-placement^='top'] {
    .tippy-arrow {
      border-top-color: ${colors.NightBlue} !important;
    }
  }
  &[data-placement^='right'] {
    .tippy-arrow {
      border-top-color: ${colors.NightBlue} !important;
    }
  }
  &[data-placement^='bottom'] {
    .tippy-arrow {
      border-top-color: ${colors.NightBlue} !important;
    }
  }
  &[data-placement^='left'] {
    .tippy-arrow {
      border-top-color: ${colors.NightBlue} !important;
    }
  }

  /* Green style tooltip */
  &.greenStyle {
    background: ${colors.AccentGreen} !important;

    .tippy-arrow {
      color: ${colors.AccentGreen};
    }
    /* Styling the arrow for different placements */
    &[data-placement^='top'] {
      .tippy-arrow {
        border-top-color: ${colors.AccentGreen} !important;
      }
    }
  }
`;

const Icon = styled(IconCopy)`
  position: absolute;
  right: -9px;
  top: -8px;
`;

export const SpanWrapper = styled('span')<any>`
  ${({ isVertical }): string =>
    isVertical &&
    `
    display: flex;
    flex-direction: column;
    width: 30px;
  `}
  ${({ isAligned }): string =>
    isAligned &&
    `
    display: flex;
    align-items: center;
  `}
  ${({ withDashedLine }): string =>
    withDashedLine &&
    `
    border-bottom: dashed 1px;
  `}
  ${({ isBlockStretched }): string =>
    isBlockStretched &&
    `
      min-width: 100%;
  `}
`;

const ContentWrapper = styled.div<{
  maxHeight?: string;
  withDashedLine?: boolean;
}>`
  ${({ maxHeight }): any =>
    maxHeight &&
    css`
      max-height: ${maxHeight || 'auto'};
      overflow-y: auto;
      padding: 0 ${({ theme }): string => theme.spacing.small};

      ${scrollBar};
    `}
  ${({ withDashedLine }): any =>
    withDashedLine &&
    css`
      font-size: 13px;
      line-height: 15px;
      padding: 7px 8px;
      font-weight: 400;
    `}
`;

interface ITippyToolTipProps {
  tooltipContent: any;
  children: any;
  offset?: [number, number] | undefined;
  delay?: number | [number | null, number | null] | undefined;
  duration?: number | [number | null, number | null] | undefined;
  interactive?: boolean;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'left';
  trigger?: string;
  appendTo?: any;
  arrow?: boolean;
  aria?: {
    content: 'describedby' | 'labelledby';
    expanded: boolean | 'auto';
  };
  inertia?: boolean;
  enabled?: boolean;
  disabled?: boolean;
  className?: string;
  canBeCopied?: boolean;
  smartTooltipRef?: RefObject<HTMLDivElement> | null;
  isVertical?: boolean;
  maxWidth?: string;
  isFlipping?: boolean;
  zIndex?: string;
  isAligned?: boolean;
  withDashedLine?: boolean;
  maxHeight?: string;
  isBlockStretched?: boolean;
}

/**
 * TippyToolTip abstracts away the configuration to use a Tippy tooltip
 * You can still import Tippy manually if you want to customize the way content
 * is rendered, but this component only provides Opus defaults for Tooltip
 * components. Changing the configuration here will change it everywhere.
 * You can override props however you like, e.g. override the distance value which
 * is now set to a sane default for KPI components
 * @param props
 * @param props.tooltipContent The value to show in the tooltip. Can be a string or any valid JSX component
 * @param props.children The child components to wrap around
 */
const TippyToolTip: React.FC<ITippyToolTipProps> = (props) => {
  const {
    tooltipContent,
    children,
    offset = [0, 7], // the size of the caret
    delay = [750, 125],
    duration = [300, 125],
    interactive = true,
    placement = 'top',
    trigger = 'mouseenter',
    appendTo = document.body, // Needs to be document-body if you use this in a hyperlink
    arrow = true,
    aria = {
      content: null,
      expanded: 'auto',
    },
    inertia = true,
    className = undefined,
    canBeCopied = false,
    enabled,
    smartTooltipRef,
    isVertical,
    maxWidth,
    isFlipping = true,
    zIndex,
    isAligned,
    withDashedLine,
    maxHeight,
    isBlockStretched,
  } = props;

  const [isCopied, resetIsCopied, copy] = useCopyToClipboard(tooltipContent);
  const [tippy, setTippy] = useState<Instance>();
  const timerRef = useRef<any>();

  const onScrollListener = useCallback((): void => {
    if (tippy?.state.isShown) {
      tippy?.hide();
    }
  }, [tippy]);

  useEffect(() => {
    window.addEventListener('scroll', onScrollListener);
    return (): void => {
      window.removeEventListener('scroll', onScrollListener);
    };
  }, [onScrollListener]);

  const onCopy = (event: any): void => {
    event.preventDefault();
    event.stopPropagation();
    copy();
    timerRef.current = setTimeout(() => {
      tippy?.hide();
    }, 2000);
  };

  const onDestroy = (): void => {
    clearTimeout(timerRef.current);
    resetIsCopied();
  };

  const tooltipContentNode = canBeCopied ? (
    !isCopied ? (
      <ContentWrapper
        onClick={onCopy}
        ref={smartTooltipRef}
        maxHeight={maxHeight}
      >
        <Icon />
        {tooltipContent}
      </ContentWrapper>
    ) : (
      'Copied!'
    )
  ) : (
    <ContentWrapper maxHeight={maxHeight} withDashedLine={withDashedLine}>
      {tooltipContent}
    </ContentWrapper>
  );

  /**
   * You can style a Tippy component imported from @tippy.js/react in two ways
   * 1: You can add a new global stylesheet to the app and reference it by name with the theme prop
   * e.g. add a new .css file, import it and reference the className 'opus'.
   * 2. You can use Styled Components by wrapping the original Tippy component with the
   * styled() method. That's how we solve the special Opus theming for now.
   * That's why when you pass the string 'opus' as the theme prop (following the API design)
   * from Tippy's documentation, we switch it out for a Styled Component.
   */

  return (
    <StyledTippy
      content={tooltipContentNode}
      placement={placement}
      trigger={trigger}
      arrow={arrow}
      animation={'shift-away'}
      appendTo={appendTo}
      aria={aria}
      inertia={inertia}
      interactive={interactive}
      duration={duration}
      delay={delay}
      offset={offset}
      className={isCopied ? 'greenStyle' : className}
      onDestroy={onDestroy}
      onHidden={resetIsCopied}
      disabled={!enabled}
      onCreate={(instance: Instance): void => setTippy(instance)}
      maxWidth={maxWidth}
      popperOptions={{ modifiers: [{ name: 'flip', enabled: isFlipping }] }}
      zIndex={zIndex}
    >
      <SpanWrapper
        data-tippy-root="true"
        isVertical={isVertical}
        isAligned={isAligned}
        withDashedLine={withDashedLine}
        isBlockStretched={isBlockStretched}
        className={className}
      >
        {children}
      </SpanWrapper>
    </StyledTippy>
  );
};

export default TippyToolTip;
